import React, {useState} from 'react';
import {
    Button,
    DropdownV2,
    Flex,
    Icon,
    ListItem,
    PriorityIcon,
    Scrollbar,
    Separator,
    Text
} from '@priority-software/priority-style-react';
import styles from '../styles/buttons.module.scss';
import cx from 'classnames';
import {MoveDirection} from '../enums';
import text from '../texts';
import {priority} from '@priority-software/priority-proto-api/lib';
import {ModernTooltip} from '../../../components/Common/Tooltip/ModernTooltip';

type ButtonsProps = {
    selectedCount: number;
    resetSelected: () => void;
    moveItems: (dir: MoveDirection) => void;
    data?: priority.netitems.form.RowViewTabsDesign[];
    moveItemsBetweenTabs?: (tabIndex: number) => void;
    hideSelected: () => void;
};

export const Buttons = ({
    selectedCount,
    data,
    resetSelected,
    moveItems,
    moveItemsBetweenTabs,
    hideSelected
}: ButtonsProps) => {
    return (
        <Flex componentClasses={styles.buttonsContainer}>
            <Flex justifyContent="flex-start">
                <Text skin="paragraph-2" componentClasses={styles.counter}>
                    {text.selectedCount.replace('X', selectedCount)}
                </Text>
                <Button skin="text" componentClasses={styles.unselect} onClick={resetSelected}>
                    {text.unselect}
                </Button>
            </Flex>
            <Flex justifyContent="flex-end">
                {data && moveItemsBetweenTabs && data.length > 1 && (
                    <MoveToButton
                        disabled={selectedCount === 0}
                        data={data}
                        moveItemsBetweenTabs={moveItemsBetweenTabs}
                    />
                )}
                <Button
                    skin="text"
                    componentClasses={styles.hideButton}
                    disabled={selectedCount === 0}
                    onClick={hideSelected}
                >
                    {text.hide}
                </Button>
                <Separator align="horizontal" height={30} />
                <ModernTooltip position="top" tooltip={text.moveToTop}>
                    <Button
                        skin="text"
                        componentClasses={styles.moveButtonFirst}
                        icon={PriorityIcon.moveUp}
                        disabled={selectedCount === 0}
                        onClick={() => moveItems(MoveDirection.TOP)}
                    ></Button>
                </ModernTooltip>
                <ModernTooltip position="top" tooltip={text.moveUpOne}>
                    <Button
                        skin="text"
                        componentClasses={cx(styles.moveButton)}
                        icon={PriorityIcon.switchArrowDown}
                        disabled={selectedCount === 0}
                        onClick={() => moveItems(MoveDirection.UP)}
                    ></Button>
                </ModernTooltip>
                <ModernTooltip position="top" tooltip={text.moveDownOne}>
                    <Button
                        skin="text"
                        componentClasses={cx(styles.moveButton)}
                        icon={PriorityIcon.switchArrowUp}
                        disabled={selectedCount === 0}
                        onClick={() => moveItems(MoveDirection.DOWN)}
                    ></Button>
                </ModernTooltip>
                <ModernTooltip position="top" tooltip={text.moveToBottom}>
                    <Button
                        skin="text"
                        componentClasses={cx(styles.moveButton)}
                        icon={PriorityIcon.moveDown}
                        disabled={selectedCount === 0}
                        onClick={() => moveItems(MoveDirection.BOTTOM)}
                    ></Button>
                </ModernTooltip>
            </Flex>
        </Flex>
    );
};

const MoveToButton = ({disabled, data, moveItemsBetweenTabs}) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <DropdownV2
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            width={'contentWidth'}
            trigger={
                <Button
                    skin="text"
                    componentClasses={styles.hideButton}
                    suffixIcon={
                        <Icon
                            icon={PriorityIcon.arrowBottom}
                            componentClasses={cx({[styles.upButtonRevert]: isOpen})}
                        />
                    }
                    onClick={() => setIsOpen(isOpen => !isOpen)}
                    disabled={disabled}
                >
                    {text.moveTo}
                </Button>
            }
        >
            <Scrollbar autoHeight>
                {data.map((item, index) => {
                    return (
                        <ListItem
                            key={item.Title}
                            onClick={() => {
                                moveItemsBetweenTabs(index);
                                setIsOpen(false);
                            }}
                            isActive={false}
                            isFocused={false}
                            componentClasses={styles.listItem}
                        >
                            {item.Title}
                        </ListItem>
                    );
                })}
            </Scrollbar>
        </DropdownV2>
    );
};
